/**
 * AdminReferralAnalytics — /admin/referrals
 *
 * Admin-only per-code chart of personal referral signups. Mirrors the
 * access-code analytics shape: enter a code, get a day-by-day sparkline
 * plus top-line stats (total signups, credits paid out).
 */
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, BarChart3, Users, Coins, Sparkles, Trash2, Shield } from 'lucide-react';

const AdminReferralAnalytics = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referees, setReferees] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  const [detaching, setDetaching] = useState(null);
  // Filter — flip on to hide referees with zero fraud signals so the admin
  // can triage suspicious signups without scrolling the whole list.
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const fetchReferees = async (c) => {
    setRefLoading(true);
    try {
      const tk = window.localStorage.getItem('token');
      const { data: resp } = await axios.get(
        `${API}/admin/referrals/analytics/${encodeURIComponent(c)}/referees?limit=50`,
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {} },
      );
      setReferees(resp.referees || []);
    } catch { setReferees([]); }
    finally { setRefLoading(false); }
  };

  const detachReferee = async (row, reverse) => {
    const reason = window.prompt(
      `Detach signup by ${row.referee_email}?\n\n` +
      (reverse ? '10 credits will be reversed from the referrer.\n' : '') +
      'Reason (logged for compliance):',
      reverse ? 'Fraudulent signup' : 'Attribution correction',
    );
    if (reason === null) return; // cancelled
    setDetaching(row.id);
    try {
      const tk = window.localStorage.getItem('token');
      const { data: resp } = await axios.post(
        `${API}/admin/referrals/detach`,
        { referral_id: row.id, reverse_credits: reverse, reason },
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {} },
      );
      toast.success(`Detached · ${resp.reversed_credits || 0} credits reversed`);
      setReferees((prev) => prev.filter((r) => r.id !== row.id));
      if (data?.code) {
        fetchAnalytics({ preventDefault: () => {} });
        fetchAuditLog(data.code);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detach failed');
    } finally {
      setDetaching(null);
    }
  };

  const [auditLog, setAuditLog] = useState([]);
  const fetchAuditLog = async (c) => {
    try {
      const tk = window.localStorage.getItem('token');
      const { data: resp } = await axios.get(
        `${API}/admin/referrals/detach-log?code=${encodeURIComponent(c)}&limit=25`,
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {} },
      );
      setAuditLog(resp.entries || []);
    } catch { setAuditLog([]); }
  };

  const fetchAnalytics = async (e) => {
    e?.preventDefault();
    const c = (code || '').trim().toUpperCase();
    if (!c) { toast.error('Enter a referral code'); return; }
    setLoading(true);
    try {
      const tk = window.localStorage.getItem('token');
      const { data: resp } = await axios.get(
        `${API}/admin/referrals/analytics/${encodeURIComponent(c)}?days=${days}`,
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {} },
      );
      setData(resp);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const maxCount = data ? Math.max(1, ...data.days.map((d) => d.count)) : 1;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-referral-analytics">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto px-6 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="-ml-2" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
          </Button>
          <span className="text-sm font-bold text-foreground">Referral analytics</span>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="pro-card p-5" data-testid="analytics-search-card">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Per-code signup chart</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Look up any member's personal referral code (e.g. <code className="font-mono">TEST-P6ZJ</code>) to see day-by-day signups
            and total credits paid out.
          </p>
          <form onSubmit={fetchAnalytics} className="mt-4 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Referral code</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="STEM-XXXX"
                className="h-11 mt-1 font-mono uppercase tracking-widest"
                data-testid="ref-code-input"
              />
            </div>
            <div className="w-32">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Window</label>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="h-11 mt-1 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="days-select"
              >
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>
            <Button
              type="submit"
              disabled={loading || !code.trim()}
              className="h-11 rounded-full font-bold px-6"
              data-testid="fetch-analytics-btn"
            >
              {loading ? 'Loading…' : 'Look up'}
            </Button>
          </form>
        </div>

        {data && (
          <>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="analytics-stats">
              <Stat
                icon={<Users className="w-4 h-4" />}
                label="Signups in window"
                value={data.total_in_window.toLocaleString()}
                sub={`over ${data.days.length} day${data.days.length === 1 ? '' : 's'}`}
                testId="stat-window-total"
              />
              <Stat
                icon={<Sparkles className="w-4 h-4" />}
                label="Lifetime signups"
                value={data.total_referrals.toLocaleString()}
                sub={`${data.total_direct} direct · ${data.total_cert} via cert`}
                testId="stat-lifetime"
              />
              <Stat
                icon={<Coins className="w-4 h-4" />}
                label="Credits paid out"
                value={`+${data.credits_paid.toLocaleString()}`}
                sub={`granted to ${data.referrer?.display_name || 'referrer'}`}
                testId="stat-credits-paid"
              />
            </div>

            <div className="mt-6 pro-card p-5" data-testid="analytics-chart">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Daily signups</div>
                  <div className="font-heading text-lg font-bold">
                    Code <span className="font-mono">{data.code}</span>
                    <span className="ml-2 font-normal text-muted-foreground">
                      · owned by {data.referrer?.display_name} ({data.referrer?.email})
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-end gap-1 h-32" data-testid="chart-bars">
                {data.days.map((d) => {
                  const h = d.count === 0 ? 2 : Math.max(4, (d.count / maxCount) * 100);
                  return (
                    <div
                      key={d.day}
                      className="flex-1 min-w-0 group relative flex flex-col justify-end"
                      title={`${d.day}: ${d.count} signup${d.count === 1 ? '' : 's'}`}
                    >
                      <div
                        className={`w-full rounded-t ${d.count > 0 ? 'bg-primary' : 'bg-muted'}`}
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>{data.days[0]?.day}</span>
                <span>{data.days[data.days.length - 1]?.day}</span>
              </div>
            </div>

            {/* Detach controls — one row per referee, newest first. Admin
                can drop a fraudulent signup and (optionally) reverse the
                10 credits granted to the referrer. */}
            <div className="mt-6 pro-card p-5" data-testid="referees-list">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Recent referees · detach if fraudulent</div>
                  <div className="font-heading text-lg font-bold">Referral off-ramp</div>
                </div>
                <div className="flex items-center gap-2">
                  {referees.length > 0 && (() => {
                    const flaggedCount = referees.filter((r) => (r.fraud_signals || []).length > 0).length;
                    return (
                      <label
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-8 text-[11px] font-bold cursor-pointer select-none transition-colors ${
                          flaggedOnly
                            ? 'bg-red-500/10 border-red-500/50 text-red-700 dark:text-red-300'
                            : 'bg-background border-border text-muted-foreground hover:border-red-500/40'
                        }`}
                        data-testid="flagged-only-toggle"
                        title={`${flaggedCount} of ${referees.length} referees have fraud signals`}
                      >
                        <input
                          type="checkbox"
                          checked={flaggedOnly}
                          onChange={(e) => setFlaggedOnly(e.target.checked)}
                          className="w-3.5 h-3.5 accent-red-600"
                          data-testid="flagged-only-checkbox"
                        />
                        <Shield className="w-3 h-3" />
                        Flagged only ({flaggedCount})
                      </label>
                    );
                  })()}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchReferees(data.code)}
                    disabled={refLoading}
                    className="h-8 rounded-full font-bold px-3"
                    data-testid="load-referees-btn"
                  >
                    {refLoading ? 'Loading…' : (referees.length ? 'Refresh' : 'Load referees')}
                  </Button>
                </div>
              </div>
              {(() => {
                // Apply the flagged-only filter here so an admin can hide
                // clean rows and just deal with the risky pile.
                const visible = flaggedOnly
                  ? referees.filter((r) => (r.fraud_signals || []).length > 0)
                  : referees;
                if (referees.length === 0) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      {refLoading ? 'Loading…' : 'Tap "Load referees" to review the signups credited to this code.'}
                    </p>
                  );
                }
                if (visible.length === 0) {
                  return (
                    <p className="text-xs text-muted-foreground" data-testid="no-flagged-referees">
                      No flagged signups in this batch — every referee looks legitimate.
                    </p>
                  );
                }
                return (
                <ul className="divide-y divide-border" data-testid="referees-rows">
                  {visible.map((r) => (
                    <li key={r.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`referee-row-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm font-bold text-foreground truncate">{r.referee_email || r.referee_user_id}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {new Date(r.created_at).toLocaleString()} ·
                          <span className={`ml-1 font-bold ${
                            r.referee_status === 'banned'    ? 'text-red-600' :
                            r.referee_status === 'suspended' ? 'text-amber-600' :
                            'text-emerald-600'
                          }`}>
                            {(r.referee_status || 'active').toUpperCase()}
                          </span>
                          {r.source === 'retro_attach' && <span className="ml-1 text-primary">· retro-attached</span>}
                        </div>
                        {(r.fraud_signals || []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`fraud-signals-${r.id}`}>
                            {r.fraud_signals.map((s) => (
                              <span
                                key={s}
                                className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border border-red-500/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                                title={s === 'same_ip_5min' ? 'Same IP as referrer within 5 minutes' : 'Same IP as referrer'}
                              >
                                <Shield className="w-2.5 h-2.5" /> {s.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => detachReferee(r, false)}
                          disabled={detaching === r.id}
                          className="h-8 rounded-full font-bold px-3 text-xs"
                          data-testid={`detach-only-${r.id}`}
                          title="Remove attribution but keep the referrer's 10 credits"
                        >
                          <Shield className="w-3.5 h-3.5 mr-1" /> Detach only
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => detachReferee(r, true)}
                          disabled={detaching === r.id}
                          className="h-8 rounded-full font-bold px-3 text-xs bg-red-500 hover:bg-red-600 text-white"
                          data-testid={`detach-reverse-${r.id}`}
                          title="Remove attribution AND reverse the referrer's 10 credits"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> {detaching === r.id ? '…' : 'Detach & reverse'}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
                );
              })()}
            </div>

            {/* Detach audit log — compliance trail. Fetch on-demand so the
                page loads snappily even when the log is deep. */}
            <div className="mt-6 pro-card p-5" data-testid="detach-audit-log">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Compliance trail</div>
                  <div className="font-heading text-lg font-bold">Detach audit log</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchAuditLog(data.code)}
                  className="h-8 rounded-full font-bold px-3"
                  data-testid="load-audit-log-btn"
                >
                  {auditLog.length ? 'Refresh' : 'Load log'}
                </Button>
              </div>
              {auditLog.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No detach actions logged for this code yet.
                </p>
              ) : (
                <ul className="divide-y divide-border" data-testid="audit-log-rows">
                  {auditLog.map((e) => (
                    <li key={e.id} className="py-2 text-xs flex items-start gap-3 flex-wrap" data-testid={`audit-row-${e.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono font-bold text-foreground truncate">{e.referee_email || e.referee_user_id}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {new Date(e.detached_at).toLocaleString()} · admin <span className="font-bold">{e.admin_email}</span>
                          {e.reason && <> · reason: <span className="italic">{e.reason}</span></>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-[11px] font-bold ${e.reverse_credits ? 'text-red-600' : 'text-muted-foreground'}`}>
                          {e.reverse_credits ? `-${e.reversed_amount || 0} credits` : 'no reversal'}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const Stat = ({ icon, label, value, sub, testId }) => (
  <div className="pro-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
      <span className="text-primary">{icon}</span> {label}
    </div>
    <div className="font-heading text-3xl font-extrabold tracking-tight mt-1">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default AdminReferralAnalytics;
